import {
  Box, Typography, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { CloudUpload, Crop, Delete, Download } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useCallback, useRef, useState } from 'react';
import type { Photo, PhotoViewMode } from '../../../types/photo';
import { PHOTO_SLOTS } from '../../../types/photo';
import { downloadSinglePhoto } from '../../../services/photoService';
import { CropDialog } from './CropDialog';
import { FilledSlot, EmptySlot, type SlotUploadState } from './PhotoSlotComponents';
import { PhotoGridMedium } from './PhotoGridMedium';

interface Props {
  viewMode: PhotoViewMode;
  photos: Photo[];
  onUpload: (original: File, cropped: File, stage: string, position: string, onProgress: (pct: number) => void) => Promise<void>;
  onSlotUpload: (file: File, stage: string, position: string, onStep?: (step: string, progress: number) => void) => Promise<void>;
  onRecrop: (photoKey: string, cropped: File, onProgress: (pct: number) => void) => Promise<void>;
  onDelete: (photoKey: string) => Promise<void>;
  onUpdateComment: (photoKey: string, comment: string | null) => Promise<void>;
  readOnly?: boolean;
}

interface CropState { imageUrl: string; fileName: string; stage: string; position: string; originalFile: File; recropPhotoKey?: string; fullSelection?: boolean; }

const STAGE_GROUPS = [
  { label: 'Pre-test', stages: ['pre-test'] },
  { label: 'Stage 1', stages: ['1'] },
  { label: 'Stage 2', stages: ['2'] },
  { label: 'Stage 3', stages: ['3'] },
  { label: 'Stage 4', stages: ['4'] },
  { label: 'Stage 5', stages: ['5'] },
];

export const PhotoGallery = ({ viewMode, photos, onUpload, onSlotUpload, onRecrop, onDelete, onUpdateComment, readOnly = false }: Props) => {
  const theme = useTheme();
  const [uploadStates, setUploadStates] = useState<Record<string, SlotUploadState>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [cropState, setCropState] = useState<CropState | null>(null);

  const slotKey = (stage: string, position: string) => `${stage}_${position}`;
  const findPhoto = (stage: string, position: string) =>
    photos.find((p) => p.Stage === stage && p.Position === position && p.UploadStatus === 'complete');

  // Auto-process new file via server-side Python pipeline
  const handleSlotFile = useCallback(async (file: File, stage: string, position: string) => {
    const key = slotKey(stage, position);
    setUploadStates((s) => ({ ...s, [key]: { progress: 0, status: 'uploading', step: 'uploading' } }));
    try {
      await onSlotUpload(file, stage, position, (step, pct) => {
        setUploadStates((s) => ({ ...s, [key]: { progress: pct, status: 'uploading', step } }));
      });
      setUploadStates((s) => ({ ...s, [key]: { progress: 100, status: 'idle' } }));
    } catch {
      setUploadStates((s) => ({ ...s, [key]: { progress: 0, status: 'error' } }));
    }
  }, [onSlotUpload]);

  // Open crop dialog for re-crop (from original)
  const openCropForRecrop = useCallback((photo: Photo) => {
    const url = photo.originalImageUrl || photo.imageUrl;
    setCropState({ imageUrl: url, fileName: photo.FileName, stage: photo.Stage, position: photo.Position, originalFile: null as any, recropPhotoKey: photo.PhotoKey });
  }, []);

  // Open crop dialog for editing the cropped version (full selection)
  const openCropForEditCropped = useCallback((photo: Photo) => {
    setCropState({ imageUrl: photo.imageUrl, fileName: photo.FileName, stage: photo.Stage, position: photo.Position, originalFile: null as any, recropPhotoKey: photo.PhotoKey, fullSelection: true });
  }, []);

  // After crop confirmed
  const handleCropConfirm = useCallback(async (croppedFile: File) => {
    if (!cropState) return;
    const { stage, position, originalFile, recropPhotoKey } = cropState;
    const key = slotKey(stage, position);

    // Clean up object URL
    if (cropState.imageUrl.startsWith('blob:')) URL.revokeObjectURL(cropState.imageUrl);
    setCropState(null);

    setUploadStates((s) => ({ ...s, [key]: { progress: 0, status: 'uploading' } }));
    try {
      if (recropPhotoKey) {
        await onRecrop(recropPhotoKey, croppedFile, (pct) => {
          setUploadStates((s) => ({ ...s, [key]: { progress: pct, status: 'uploading' } }));
        });
      } else {
        await onUpload(originalFile, croppedFile, stage, position, (pct) => {
          setUploadStates((s) => ({ ...s, [key]: { progress: pct, status: 'uploading' } }));
        });
      }
      setUploadStates((s) => ({ ...s, [key]: { progress: 100, status: 'idle' } }));
    } catch {
      setUploadStates((s) => ({ ...s, [key]: { progress: 0, status: 'error' } }));
    }
  }, [cropState, onUpload, onRecrop]);

  const handleCropCancel = useCallback(() => {
    if (cropState?.imageUrl.startsWith('blob:')) URL.revokeObjectURL(cropState.imageUrl);
    setCropState(null);
  }, [cropState]);

  const handleDrop = useCallback((e: React.DragEvent, stage: string, position: string) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('image/')) handleSlotFile(file, stage, position);
  }, [handleSlotFile]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, stage: string, position: string) => {
    const file = e.target.files?.[0];
    if (file) handleSlotFile(file, stage, position);
    e.target.value = '';
  }, [handleSlotFile]);

  // --- List view ---
  if (viewMode === 'list') {
    return (
      <>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Slot</TableCell><TableCell>Preview</TableCell>
              <TableCell>File</TableCell><TableCell>Comment</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {PHOTO_SLOTS.map((slot) => {
              const photo = findPhoto(slot.stage, slot.position);
              const key = slotKey(slot.stage, slot.position);
              const us = uploadStates[key];
              return (
                <TableRow key={key}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{slot.label}</TableCell>
                  <TableCell sx={{ width: 64 }}>
                    {photo ? (
                      <Box component="img" src={photo.thumbnailUrl || photo.imageUrl} alt={slot.label}
                        sx={{ width: 48, height: 36, objectFit: 'contain', borderRadius: 0.5 }} loading="lazy" />
                    ) : (
                      <Box sx={{ width: 48, height: 36, bgcolor: alpha(theme.palette.text.primary, 0.04), borderRadius: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: photo ? 'text.primary' : 'text.disabled' }}>
                    {photo ? photo.FileName : us?.status === 'uploading' ? `Uploading ${us.progress}%` : 'Empty'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    {photo && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{photo.Comment || '—'}</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    {photo ? (
                      <>
                        {!readOnly && <Tooltip title="Re-crop"><IconButton size="small" onClick={() => openCropForRecrop(photo)}><Crop fontSize="small" /></IconButton></Tooltip>}
                        <Tooltip title="Download"><IconButton size="small" onClick={() => downloadSinglePhoto(photo.imageUrl, photo.FileName)}><Download fontSize="small" /></IconButton></Tooltip>
                        {!readOnly && <Tooltip title="Delete"><IconButton size="small" onClick={() => onDelete(photo.PhotoKey)}><Delete fontSize="small" /></IconButton></Tooltip>}
                      </>
                    ) : !readOnly ? (
                      <>
                        <input type="file" accept="image/*" hidden ref={(el) => { fileInputRefs.current[key] = el; }}
                          onChange={(e) => handleInputChange(e, slot.stage, slot.position)} />
                        <Tooltip title="Upload"><IconButton size="small" onClick={() => fileInputRefs.current[key]?.click()}><CloudUpload fontSize="small" /></IconButton></Tooltip>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {cropState && <CropDialog open imageUrl={cropState.imageUrl} fileName={cropState.fileName} initialFullSelection={cropState.fullSelection} onConfirm={handleCropConfirm} onCancel={handleCropCancel} />}
      </>
    );
  }

  // --- Medium (stage grid) view ---
  if (viewMode === 'medium') {
    return (
      <>
        <PhotoGridMedium
          photos={photos}
          findPhoto={findPhoto}
          uploadStates={uploadStates}
          onClickUpload={(stage, pos) => fileInputRefs.current[slotKey(stage, pos)]?.click()}
          onDrop={handleDrop}
          onDelete={onDelete}
          onRecrop={openCropForRecrop}
          onEditCropped={openCropForEditCropped}
          fileInputRefs={fileInputRefs}
          onInputChange={handleInputChange}
          readOnly={readOnly}
        />
        {cropState && <CropDialog open imageUrl={cropState.imageUrl} fileName={cropState.fileName} initialFullSelection={cropState.fullSelection} onConfirm={handleCropConfirm} onCancel={handleCropCancel} />}
      </>
    );
  }

  // --- Large (gallery) view ---
  return (
    <Box>
      {STAGE_GROUPS.map((group) => (
        <Box key={group.label} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{
            fontWeight: 700, mb: 1, color: 'text.secondary',
            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`, pb: 0.5,
          }}>
            {group.label}
          </Typography>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' } }}>
            {group.stages.flatMap((stage) =>
              ['top', 'bottom'].map((position) => {
                const photo = findPhoto(stage, position);
                const key = slotKey(stage, position);
                const us = uploadStates[key];

                return (
                  <Box key={key}>
                    {photo ? (
                      <FilledSlot photo={photo} theme={theme}
                        onDelete={readOnly ? undefined : () => onDelete(photo.PhotoKey)}
                        onRecrop={readOnly ? undefined : () => openCropForRecrop(photo)}
                        onEditCropped={readOnly ? undefined : () => openCropForEditCropped(photo)}
                        onUpdateComment={readOnly ? undefined : (c) => onUpdateComment(photo.PhotoKey, c)} />
                    ) : !readOnly ? (
                      <EmptySlot theme={theme} uploadState={us}
                        onDrop={(e) => handleDrop(e, stage, position)}
                        onClickUpload={() => fileInputRefs.current[key]?.click()} />
                    ) : (
                      <Box sx={{ aspectRatio: '4/3', borderRadius: 1, bgcolor: alpha(theme.palette.text.primary, 0.04),
                        border: `1px dashed ${alpha(theme.palette.text.primary, 0.12)}` }} />
                    )}
                    <input type="file" accept="image/*" hidden
                      ref={(el) => { fileInputRefs.current[key] = el; }}
                      onChange={(e) => handleInputChange(e, stage, position)} />
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', fontWeight: 600, color: 'text.secondary' }}>
                      {position === 'top' ? 'Top' : 'Bottom'}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      ))}
      {cropState && <CropDialog open imageUrl={cropState.imageUrl} fileName={cropState.fileName} initialFullSelection={cropState.fullSelection} onConfirm={handleCropConfirm} onCancel={handleCropCancel} />}
    </Box>
  );
};

/* Sub-components are in PhotoSlotComponents.tsx */
